<script setup>
import { motion, AnimatePresence } from "motion-v";
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useDevice } from "@/composables/useDevice";
import { useBettingStats } from "@/composables/useBettingStats";
import { useAuth } from "@/composables/useAuth";
import { useBet } from "@/composables/useBet";
import { useSocket } from "@/composables/useSocket";
import apiClient from "@/composables/common/axios";
import chartExampleImage from "@/assets/images/chart_example.png";

// ===== 상수 =====
const CIRCLE_RADIUS = 46;
const GAME_DURATION_MS = 30000; // 게임 총 시간 (30초로 변경 - Socket.io 서버와 동기화)
const DANGER_THRESHOLD_MS = 5000; // 위험 모드 진입 시간 (종료 5초 전)
const MID_GAME_ELAPSED_MS = 10000; // 중간 입장 테스트용 경과 시간 (10초)
const TRIGGER_THRESHOLD = 10; // 애니메이션 시작 트리거 값
const HISTORY_MAX_PC = 10;
const HISTORY_MAX_MOBILE = 3;

// ===== Props =====
const props = defineProps({
  showBettingStatus: {
    type: Boolean,
    default: false,
  },
});

// ===== Composables =====
const { isMobile } = useDevice();
const { isLoggedIn, currentUser, refreshUser } = useAuth();
const { placeBet } = useBet();
const { connect, disconnect, on, off, emit, isConnected, updateUser } = useSocket();
const { showToast } = useToast();

// ===== Refs =====
const chartCanvas = ref(null);
const historyData = ref([]);
const bettingDirections = ref([]);
const hoveredHistoryId = ref(null);
const previousHoveredId = ref(null);

// 모달 refs
const playGuideModal = ref(null);

// 가이드 모달 스텝 관리
const currentStep = ref(1);
const totalSteps = 4;

// 애니메이션 관련
const shouldStartAnimation = ref(false);
const isAnimationComplete = ref(false);
const triggerValue = ref(0);

// 게임 상태
const serverState = ref({
  gameId: null,
  status: "WAITING", // 'WAITING' | 'PLAYING' | 'FINISHED'
  startTime: null,
  endTime: null,
  dangerTime: null,
  totalDuration: GAME_DURATION_MS,
});

// 화면 표시 상태
const displayTime = ref(0);
const progressRate = ref(0);
const isDanger = ref(false);

// 선택된 배팅 금액
const selectedAmount = ref(null);

// input에 직접 입력한 값 (문자열, 콤마 포함)
const inputValue = ref("");

// 마지막 베팅 금액 (베팅 후 유지)
const lastBetAmount = ref(null);

// 각 금액별 클릭 횟수 추적
const amountClickCount = ref({
  10000: 0,
  25000: 0,
  50000: 0,
  100000: 0,
  500000: 0,
});

// 테스트 버튼 영역 표시 여부
const showTestButtons = ref(true);

// 배팅 성공 알림 표시 여부
const showBettingSuccess = ref(false);

// 잔고 주기적 갱신 타이머
const balanceInterval = ref(null);

// 배팅 마감 토스트 마지막 표시 시간
const lastBettingCloseToast = ref(0);

// 로그인 상태 변경 시 타이머 제어
watch(isLoggedIn, (newValue) => {
  if (newValue) {
    // 로그인 시 타이머 시작
    if (!balanceInterval.value) {
      balanceInterval.value = setInterval(() => {
        refreshUser();
      }, 3000);
    }
  } else {
    // 로그아웃 시 타이머 정리
    if (balanceInterval.value) {
      clearInterval(balanceInterval.value);
      balanceInterval.value = null;
    }
  }
});

// 배팅 마감 토스트 표시 (displayTime이 3초 이하가 되면)
watch(displayTime, (newTime, oldTime) => {
  const now = Date.now();

  // 3초 이하로 떨어졌을 때만 (한 라운드당 1회)
  if (newTime <= 3000 && oldTime > 3000) {
    // 25초 이상 지났을 때만 토스트 표시 (30초 라운드 기준, 라운드당 1회)
    if (now - lastBettingCloseToast.value > 25000) {
      lastBettingCloseToast.value = now;
      showToast('배팅이 마감되었습니다.', 'warning');
    }
  }
});

// ===== 계산된 값 =====
const circumference = 2 * Math.PI * CIRCLE_RADIUS;
const maxHistoryCount = computed(() =>
  isMobile.value ? HISTORY_MAX_MOBILE : HISTORY_MAX_PC
);

// ===== 더미 데이터 =====
// 더미 요율 생성 함수 (90300 ~ 90400 사이 랜덤 값)
const generateDummyRate = () => {
  const base = 90300;
  const random = Math.random() * 100;
  return (base + random).toFixed(5);
};

const apiHistoryData = [
  {
    id: 1,
    direction: "up",
    label: "승리",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 2,
    direction: "down",
    label: "패배",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 3,
    direction: "down",
    label: "패배",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 4,
    direction: "up",
    label: "승리",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 5,
    direction: "up",
    label: "승리",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 6,
    direction: "up",
    label: "승리",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 7,
    direction: "down",
    label: "패배",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 8,
    direction: "up",
    label: "승리",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 9,
    direction: "down",
    label: "패배",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 10,
    direction: "up",
    label: "승리",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
  {
    id: 11,
    direction: "up",
    label: "승리",
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  },
];

// 배팅 통계 (composable에서 가져옴)
const { bettingStats, addMyBetting, resetGameData, resetGameDataEmpty } =
  useBettingStats();

// 차트 메타 정보
const chartMeta = ref({
  winRate: 0, // 24시간 승률 (%)
  realtimePlayers: 0, // 24시간 실시간 플레이어 (명)
  dailyPayout: 0, // 24시간 승리 지급액
  totalPayout: 0, // 총 지급된 승리 금액
});

// 통계 조회
const fetchGameStats = async () => {
  try {
    const response = await apiClient.get('/api/stats.php?action=game_stats');
    if (response.data.success) {
      chartMeta.value = response.data.stats;
      // // console.log('[Stats] Loaded:', response.data.stats);
    }
  } catch (error) {
    console.error('[Stats] Fetch failed:', error);
  }
};

// ===== RequestAnimationFrame 관리 =====
let rafId = null;

// ===== Computed =====
const circleAnimation = computed(() => {
  // 음수로 해야 시계 방향으로 감소
  const offset = circumference * (progressRate.value - 1);
  return { strokeDashoffset: offset };
});

// 내가 베팅한 총 금액
const myTotalBetting = computed(() => {
  return (
    bettingStats.value.upTotalBetting + bettingStats.value.downTotalBetting
  );
});

// input에 표시할 값 (콤마 포함)
const displayInputValue = computed(() => {
  return inputValue.value;
});

// 각 금액별 클릭 횟수 getter
const getClickCount = (amount) => {
  return amountClickCount.value[amount] || 0;
};

// 정렬된 히스토리 데이터 (id 오름차순: 가장 오래된 것부터 최근 것까지)
const sortedHistoryData = computed(() => {
  return [...historyData.value].sort((a, b) => a.id - b.id);
});

// 가장 최근 게임의 id
const latestGameId = computed(() => {
  if (sortedHistoryData.value.length === 0) return null;
  return sortedHistoryData.value[sortedHistoryData.value.length - 1].id;
});

// 숫자 포맷팅 함수 (콤마 추가)
const formatNumber = (num) => {
  if (!num && num !== 0) return "";
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// 문자열에서 숫자만 추출하는 함수
const extractNumber = (str) => {
  return str.replace(/[^0-9]/g, "");
};

// 키 입력 핸들러 (숫자만 허용)
const handleKeyPress = (event) => {
  // 숫자, 백스페이스, 삭제, 화살표, 탭 키만 허용
  const allowedKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Tab",
    "Home",
    "End",
  ];

  if (allowedKeys.includes(event.key)) {
    return;
  }

  // 숫자만 허용
  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
};

// input 값 변경 핸들러
const handleInputChange = (event) => {
  const rawValue = event.target.value;
  // 숫자만 추출
  const numbersOnly = extractNumber(rawValue);

  if (numbersOnly === "") {
    inputValue.value = "";
    selectedAmount.value = null;
    return;
  }

  // 숫자로 변환
  const numValue = parseInt(numbersOnly, 10);

  // selectedAmount 업데이트
  selectedAmount.value = numValue;

  // 콤마가 포함된 형식으로 표시
  inputValue.value = formatNumber(numValue);
};

// 미리보기용 총 배팅금 (내가 베팅한 금액 + 선택한 금액)
const previewTotalBetting = computed(() => {
  const existingBet = myTotalBetting.value;
  const selected = selectedAmount.value || 0;
  return existingBet + selected;
});

// 배팅 가능 여부 (게임 진행 중이고 남은 시간이 0초 이상일 때)
const canBet = computed(() => {
  // 게임이 진행 중이어야 함
  if (!isGamePlaying.value) return false;

  // 남은 시간이 0초 초과일 때만 배팅 가능 (displayTime은 초 단위)
  return displayTime.value > 0;
});

// 배팅 마감 상태 (게임은 진행 중이지만 남은 시간이 0초 이하)
const isBettingClosed = computed(() => {
  return isGamePlaying.value && displayTime.value <= 0;
});

// 게임 타이머가 돌아가는 동안 여부
const isGamePlaying = computed(() => {
  return shouldStartAnimation.value;
});

// ===== Watchers =====
watch(triggerValue, (newValue) => {
  if (newValue > TRIGGER_THRESHOLD && !shouldStartAnimation.value) {
    shouldStartAnimation.value = true;
  }
});

watch(isMobile, () => {
  if (historyData.value.length > 0) {
    adjustHistoryData();
  }
});

// 가이드 모달 스텝 네비게이션
const goToPreviousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const goToNextStep = () => {
  if (currentStep.value < totalSteps) {
    currentStep.value++;
  }
};

const isFirstStep = computed(() => currentStep.value === 1);
const isLastStep = computed(() => currentStep.value === totalSteps);

// 모달이 열릴 때 스텝을 1로 리셋
const handleModalOpen = () => {
  currentStep.value = 1;
};

// hoveredHistoryId 변경 시 이전 요소의 애니메이션 즉시 종료
watch(hoveredHistoryId, (newId, oldId) => {
  if (oldId && oldId !== newId && process.client) {
    // 이전 요소의 애니메이션을 즉시 종료하기 위해 DOM에서 제거
    previousHoveredId.value = oldId;
    nextTick(() => {
      previousHoveredId.value = null;
    });
  }
});

// ===== 게임 루프 =====
const updateGameLoop = () => {
  if (!serverState.value.endTime) return;

  const now = Date.now();
  const timeLeftMs = serverState.value.endTime - now;

  // 게임 종료 처리
  if (timeLeftMs <= 0) {
    displayTime.value = 0;
    progressRate.value = 1;
    shouldStartAnimation.value = false;
    isDanger.value = false;
    isAnimationComplete.value = true;
    // 게임 종료 시 결과 영역 표시 (UP/DOWN 모두)
    bettingStats.value.upShowResult = true;
    bettingStats.value.downShowResult = true;
    if (rafId) cancelAnimationFrame(rafId);
    return;
  }

  // 진행률 계산 (0 ~ 1)
  const currentProgress = 1 - timeLeftMs / serverState.value.totalDuration;
  progressRate.value = Math.max(0, Math.min(1, currentProgress));

  // 화면 표시용 초 단위
  displayTime.value = Math.ceil(timeLeftMs / 1000);

  // 위험 모드 감지
  isDanger.value = now >= serverState.value.dangerTime;

  // 다음 프레임 요청
  rafId = requestAnimationFrame(updateGameLoop);
};

// ===== 더미 API 함수 =====
const fetchServerGameStatus = (type) => {
  return new Promise((resolve) => {
    const now = Date.now();
    const elapsed = type === "MID" ? MID_GAME_ELAPSED_MS : 0;
    const serverStartTime = now - elapsed;

    resolve({
      gameId: 101,
      status: "PLAYING",
      startTime: serverStartTime,
      endTime: serverStartTime + GAME_DURATION_MS,
      dangerTime: serverStartTime + GAME_DURATION_MS - DANGER_THRESHOLD_MS,
      totalDuration: GAME_DURATION_MS,
      serverTimestamp: now,
    });
  });
};

// ===== 게임 제어 =====
const syncAndStartGame = async (type) => {
  if (rafId) cancelAnimationFrame(rafId);
  isAnimationComplete.value = false;
  // 배팅 성공 알림 숨기기
  showBettingSuccess.value = false;

  const data = await fetchServerGameStatus(type);
  serverState.value = data;
  shouldStartAnimation.value = true;

  updateGameLoop();
};

// 빈 상태로 새 게임 시작 (모든 데이터 제거)
const startNewGameEmpty = async (type) => {
  // 게임 데이터 완전 초기화 (모든 데이터 제거, 빈 상태)
  resetGameDataEmpty();

  // 베팅 방향 초기화
  bettingDirections.value = [];

  // 배팅 성공 알림 숨기기
  showBettingSuccess.value = false;

  // 게임 시작
  await syncAndStartGame(type);
};

// ===== Canvas 초기화 =====
const initCanvas = () => {
  if (!chartCanvas.value) return;

  const canvas = chartCanvas.value;
  const ctx = canvas.getContext("2d");
  const img = new Image();

  img.onload = () => {
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0);
  };

  img.src = chartExampleImage;
};

// ===== Lifecycle Hooks =====
onMounted(() => {
  if (process.client) {
    initCanvas();
    fetchBettingHistory(); // 배팅 이력 조회
    fetchGameStats(); // 게임 통계 조회

    // Socket.io 연결
    const socket = connect(
      currentUser.value?.id,
      currentUser.value?.nickname,
      currentUser.value?.avatar
    );

    // 실시간 가격 업데이트
    on('price:update', (data) => {
      if (data.price) {
        // 가격 업데이트 로직 (필요시 구현)
        // // console.log('[Socket] Price:', data.price);
      }
    });

    // 라운드 정보 업데이트
    on('round:update', (data) => {
      // // console.log('[Socket] Round update:', data);
      if (data.round_id) {
        // 라운드가 변경되었으면 게임 데이터 초기화
        const isNewRound = serverState.value.gameId && serverState.value.gameId !== data.round_id;

        serverState.value.gameId = data.round_id;
        serverState.value.status = data.status === 'betting' ? 'PLAYING' : 'WAITING';
        serverState.value.startTime = data.round_start_time;

        // 새 라운드 시작 시 배팅 데이터 초기화
        if (isNewRound) {
          // // console.log('[Round] New round started, resetting game data');
          resetGameData();
        }
      }
    });

    // 타이머 틱
    on('round:tick', (data) => {
      // // console.log('[Socket] Round tick:', data);
      if (data.remaining !== undefined) {
        // // console.log('[Timer] Updating displayTime to:', data.remaining);

        // 라라곤과 동일한 로직: 5~20초일 때만 타이머 숫자 표시
        const shouldShowTimer = data.remaining > 5 && data.remaining <= 20;

        if (shouldShowTimer) {
          // 타이머 표시 (15~1초로 변환)
          displayTime.value = data.remaining - 5;
          progressRate.value = (data.remaining - 5) / 15; // 0-1 값으로 계산
          isDanger.value = data.remaining <= 10; // 10초 이하일 때 빨간색
          shouldStartAnimation.value = true;
        } else {
          // 로딩 바 표시
          shouldStartAnimation.value = false;
        }
      }
    });

    // 결과 업데이트
    on('results:update', (data) => {
      // // console.log('[Socket] Results update:', data);
      if (data.results && Array.isArray(data.results)) {
        // // console.log('[History] Received', data.results.length, 'results');
        historyData.value = data.results.map(r => ({
          id: r.id,
          direction: r.direction,
          label: r.direction === 'up' ? '승리' : '패배',
          startRate: r.startRate,
          endRate: r.endRate
        }));
      }

      // 배팅 이력도 새로고침 (결과가 확정되었으므로)
      if (currentUser.value) {
        fetchBettingHistory();
      }
    });

    // 잔액 업데이트
    on('balance:update', (data) => {
      // // console.log('[Socket] Balance update event received:', data);
      if (data.balance !== undefined) {
        const { updateBalance } = useAuth();
        const newBalance = Number(data.balance);
        // // console.log('[Socket] Updating balance to:', newBalance);
        updateBalance(newBalance);
        // // console.log('[Socket] Balance update completed');
      } else {
        // console.warn('[Socket] Balance update event missing balance value:', data);
      }
    });

    // 배팅 결과
    on('bet:result', (data) => {
      // // console.log('[Socket] Bet result:', data);
    });

    // 정산 결과
    on('bet:settled', (data) => {
      // // console.log('[Socket] Bet settled:', data);

      // 배팅 이력 즉시 새로고침
      fetchBettingHistory();

      // 잔액 새로고침 (서버에서 balance:update도 보내지만 확실하게)
      if (isLoggedIn.value) {
        const { refreshUser } = useAuth();
        refreshUser().then(() => {
          // console.log('[Bet Settled] User balance refreshed');
        });
      }

      // 결과 알림 표시 로직
      if (data.result === '승리') {
        showBettingSuccess.value = true;
        setTimeout(() => {
          showBettingSuccess.value = false;
        }, 3000);
      }
    });

    // 현재 라운드 배팅 정보 업데이트
    on('bets:update', (data) => {
      // // console.log('[Socket] Bets update:', data);

      // UP/DOWN 배팅 금액 업데이트
      bettingStats.value.upWin = data.upTotal || 0;
      bettingStats.value.downLose = data.downTotal || 0;

      // 참여자 수 업데이트
      bettingStats.value.upPeopleCount = data.upCount || 0;
      bettingStats.value.downPeopleCount = data.downCount || 0;

      // 참여자 목록 업데이트 (서버에서 받은 데이터 사용)
      if (data.upBets && Array.isArray(data.upBets)) {
        // 내 배팅을 찾아서 isMy: true 설정
        bettingStats.value.upParticipants = data.upBets.map(bet => {
          const isMyBet = currentUser.value && bet.id === `user-${currentUser.value.id}`;
          return {
            ...bet,
            isMy: isMyBet
          };
        });
      }

      if (data.downBets && Array.isArray(data.downBets)) {
        // 내 배팅을 찾아서 isMy: true 설정
        bettingStats.value.downParticipants = data.downBets.map(bet => {
          const isMyBet = currentUser.value && bet.id === `user-${currentUser.value.id}`;
          return {
            ...bet,
            isMy: isMyBet
          };
        });
      }
    });

    // 소켓 연결 및 이벤트 리스너 설정 후 히스토리 데이터 요청
    fetchHistoryData();

    // 잔고 주기적 갱신 (3초마다)
    if (isLoggedIn.value) {
      balanceInterval.value = setInterval(() => {
        refreshUser();
      }, 3000);
    }
  }
});

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId);

  // 잔고 갱신 타이머 정리
  if (balanceInterval.value) {
    clearInterval(balanceInterval.value);
    balanceInterval.value = null;
  }

  // Socket.io 이벤트 리스너 제거
  off('price:update');
  off('round:update');
  off('round:tick');
  off('results:update');
  off('balance:update');
  off('bet:result');
  off('bet:settled');
  off('bets:update');

  // 연결 해제 (필요시)
  // disconnect();
});

// ===== 로그인 후 chat:join 재전송 (한 번만) =====
let userUpdated = false;
watch([currentUser, isConnected], ([user, connected]) => {
  if (!userUpdated && user?.id && connected) {
    updateUser(user.id, user.nickname, user.avatar);
    userUpdated = true;
  }
});

// ===== 배팅 핸들러 =====
const handleBet = async (direction) => {
  // canBet computed에서 이미 체크됨 (isGamePlaying && displayTime >= 3)
  // 버튼이 disabled 상태면 여기까지 오지 않음

  // 로그인 확인
  if (!isLoggedIn.value) {
    alert('로그인이 필요합니다.');
    return;
  }

  // 선택된 금액 사용
  const amount = selectedAmount.value;

  if (!amount || amount < 1000) {
    alert('베팅 금액을 선택해주세요.');
    return;
  }

  // API 호출하여 실제 베팅 처리
  const result = await placeBet(direction.toUpperCase(), amount, serverState.value.gameId);

  // // console.log('[Bet] 배팅 결과:', result);

  if (!result.success) {
    alert(result.message || '베팅에 실패했습니다.');
    return;
  }

  // 베팅 성공 - UI 업데이트
  // 방향에 따라 내 배팅 금액 및 전체 금액 업데이트
  if (direction === "up") {
    // 내가 배팅한 총 금액 누적
    bettingStats.value.upTotalBetting += amount;
    // 전체 배팅 금액에 추가 (더미 + 내 금액)
    bettingStats.value.upWin += amount;
    // 최초 1회만 참여자 수 증가 (같은 방향에 중복 베팅 방지)
    if (!bettingDirections.value.includes("up")) {
      bettingStats.value.upPeopleCount += 1;
    }
    // 예상 수익 업데이트 (전체 금액 기준)
    bettingStats.value.upExpectedProfit = Math.floor(
      bettingStats.value.upWin * 1.5
    );
  } else {
    // 내가 배팅한 총 금액 누적
    bettingStats.value.downTotalBetting += amount;
    // 전체 배팅 금액에 추가 (더미 + 내 금액)
    bettingStats.value.downLose += amount;
    // 최초 1회만 참여자 수 증가 (같은 방향에 중복 베팅 방지)
    if (!bettingDirections.value.includes("down")) {
      bettingStats.value.downPeopleCount += 1;
    }
    // 예상 수익 업데이트 (전체 금액 기준)
    bettingStats.value.downExpectedProfit = Math.floor(
      bettingStats.value.downLose * 1.5
    );
  }

  // 베팅 방향 추가
  bettingDirections.value.push(direction);

  // 내 베팅 정보를 참여자 목록 맨 위에 추가
  addMyBetting(direction, amount, currentUser.value);

  // 잔액 업데이트 (API 응답에서 받은 새 잔액)
  if (result.new_balance !== undefined) {
    const { updateBalance } = useAuth();
    updateBalance(result.new_balance);
    // // console.log('[Bet] Balance updated:', result.new_balance);
  }

  // 마지막 베팅 금액 저장
  lastBetAmount.value = amount;

  // 베팅 후 선택된 금액 및 input 초기화
  selectedAmount.value = null;
  inputValue.value = "";

  // 모든 금액별 클릭 횟수 초기화
  Object.keys(amountClickCount.value).forEach((key) => {
    amountClickCount.value[key] = 0;
  });

  // 배팅 성공 알림 표시 (3초 후 자동 닫힘)
  showBettingSuccess.value = true;
  setTimeout(() => {
    showBettingSuccess.value = false;
  }, 3000);
};

// 배팅 금액 선택 핸들러
const handleAmountClick = (amount) => {
  // 다른 금액 버튼의 클릭 횟수 초기화
  Object.keys(amountClickCount.value).forEach((key) => {
    const keyAmount = parseInt(key, 10);
    if (keyAmount !== amount) {
      amountClickCount.value[keyAmount] = 0;
    }
  });

  // 현재 금액의 클릭 횟수 증가
  amountClickCount.value[amount] += 1;

  // 누적 금액 계산
  const totalAmount = amount * amountClickCount.value[amount];

  // 선택된 금액 및 input 업데이트
  selectedAmount.value = totalAmount;
  inputValue.value = formatNumber(totalAmount);
};

// 초기화 핸들러
const handleReset = () => {
  if (selectedAmount.value) {
    selectedAmount.value = null;
    inputValue.value = "";
    lastBetAmount.value = null;
    // 모든 금액별 클릭 횟수 초기화
    Object.keys(amountClickCount.value).forEach((key) => {
      amountClickCount.value[key] = 0;
    });
  }
};

// ===== 히스토리 관리 =====
const findOldestItemIndex = () => {
  return historyData.value.reduce((minIndex, current, index, arr) => {
    return current.id < arr[minIndex].id ? index : minIndex;
  }, 0);
};

const addHistoryItem = (direction, label, id) => {
  // 최대 개수 초과 시 가장 오래된 항목 삭제
  if (historyData.value.length >= maxHistoryCount.value) {
    const oldestIndex = findOldestItemIndex();
    historyData.value.splice(oldestIndex, 1);
  }

  // 새로운 항목 추가
  historyData.value.push({
    id,
    direction,
    label: label || (direction === "up" ? "승리" : "패배"),
    startRate: generateDummyRate(),
    endRate: generateDummyRate(),
  });
};

// 배팅 이력 데이터
const bettingHistory = ref([]);

// 배팅 이력 API 조회
const fetchBettingHistory = async () => {
  try {
    // 로그인 안되어 있으면 호출하지 않음
    if (!currentUser.value) {
      // // console.log('[Betting History] Not logged in');
      bettingHistory.value = [];
      return;
    }

    const response = await apiClient.get('/api/user.php?action=history&limit=20');

    if (response.data.success) {
      bettingHistory.value = response.data.history;
      // // console.log('[Betting History] Loaded:', bettingHistory.value.length, 'items');
    } else {
      if (response.data.message === '로그인이 필요합니다.') {
        // // console.log('[Betting History] Login required');
        bettingHistory.value = [];
      } else {
        console.error('[Betting History] Error:', response.data.message);
      }
    }
  } catch (error) {
    console.error('[Betting History] Fetch failed:', error);
    bettingHistory.value = [];
  }
};

// 히스토리 데이터 조회 (게임 결과)
const fetchHistoryData = async () => {
  const { emit, isConnected } = useSocket();

  // 소켓이 연결될 때까지 기다림 (최대 5초)
  let retries = 0;
  while (!isConnected.value && retries < 10) {
    await new Promise(resolve => setTimeout(resolve, 500));
    retries++;
  }

  if (isConnected.value) {
    emit('get:history');
  }
};

// 히스토리 데이터 개수 조정
const adjustHistoryData = () => {
  if (historyData.value.length > maxHistoryCount.value) {
    const sortedData = [...historyData.value].sort((a, b) => a.id - b.id);
    const itemsToRemove = sortedData.slice(
      0,
      historyData.value.length - maxHistoryCount.value
    );
    const idsToRemove = new Set(itemsToRemove.map((item) => item.id));

    historyData.value = historyData.value.filter(
      (item) => !idsToRemove.has(item.id)
    );
  }
};

defineExpose({
  syncAndStartGame,
  triggerValue,
  isAnimationComplete, // 애니메이션 완료 상태
  addHistoryItem, // 히스토리 추가 함수
  fetchHistoryData, // 히스토리 데이터 조회 함수
});
</script>
<template>
  <div class="chart">
    <div class="chart__container">
      <!-- 헤더 영역 (BTC 로고, 제목, 도움말 버튼) -->
      <section class="chart__header">
        <div class="chart__header-row">
          <!-- BTC 로고 -->
          <img
            src="@/assets/images/btc_logo.svg"
            alt="BTC"
            class="chart__logo"
          />
          <!-- 차트 제목 -->
          <h2 class="chart__title">
            BTC<span class="chart__sub-title">/USDT</span>
          </h2>
          <!-- 도움말 버튼 -->
          <button
            type="button"
            class="chart__btn-help ico-before ico-question"
            @click="
              () => {
                playGuideModal?.open();
                handleModalOpen();
              }
            "
          >
            <span class="blind">작동방식</span>
          </button>
        </div>
      </section>

      <!-- 통계 영역 (UP/DOWN 배팅 정보, 타이머) -->
      <section class="chart__section chart__section--stats">
        <div class="chart__stats">
          <!-- UP 배팅 카드 -->
          <div class="chart__stat-card chart__stat-card--up">
            <!-- UP 배팅 정보 (총 배팅금, 예상 수익) -->
            <dl class="chart__stat-info">
              <dt>총 배팅금</dt>
              <dd>
                <CommonRollingCounter
                  :value="bettingStats.upTotalBetting"
                  prefix="₩"
                  typography="h4"
                />
              </dd>
              <dt v-if="!isMobile">예상 수익</dt>
              <dd v-if="!isMobile">
                <CommonRollingCounter
                  :value="bettingStats.upExpectedProfit"
                  prefix="₩"
                  typography="h4"
                />
              </dd>
            </dl>

            <!-- UP 결과 표시 영역 -->
            <div class="chart__stat-result">
              <div class="chart__stats-group">
                <!-- 내가 UP에 베팅했을 때 표시되는 아이콘 -->
                <motion.div
                  v-if="bettingDirections.includes('up')"
                  :initial="{ opacity: 0, scale: 0, x: 0, y: 0 }"
                  :animate="{ opacity: 1, scale: 1, x: 0, y: 0 }"
                  :transition="{
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                    mass: 0.8,
                    bounce: 0.6,
                  }"
                >
                  <img src="@/assets/images/user.png" alt="" />
                </motion.div>
                <!-- UP 배지 -->
                <span class="badge badge--up ico-after ico-up">UP 승리</span>
              </div>
              <!-- UP 총 배팅금 (K 단위) -->
              <strong class="chart__stat-value point-primary">
                <CommonRollingCounter
                  :value="Math.floor(bettingStats.upWin / 1000)"
                  suffix="K"
                  typography="h1"
                />
              </strong>
            </div>
          </div>

          <!-- 게임 타이머 영역 -->
          <div class="chart__timer">
            <!-- 타이머 원형 SVG -->
            <svg
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <!-- 타이머 배경 원 -->
              <circle
                cx="50"
                cy="50"
                r="46"
                class="chart__timer-circle chart__timer-circle--bg"
              />
              <!-- 타이머 진행 원 (애니메이션) -->
              <motion.circle
                cx="50"
                cy="50"
                r="46"
                :class="[
                  'chart__timer-circle',
                  'chart__timer-circle--fg',
                  shouldStartAnimation && 'chart__timer-circle--warning',
                  isDanger && 'chart__timer-circle--danger',
                ]"
                :stroke-dasharray="circumference"
                :animate="
                  shouldStartAnimation
                    ? circleAnimation
                    : { strokeDashoffset: 0 }
                "
                :transition="{ duration: 0 }"
                transform="rotate(-90 50 50)"
                stroke-linecap="round"
              />
            </svg>
            <!-- 타이머 텍스트 (게임 진행 중일 때만 표시) -->
            <motion.div
              v-if="shouldStartAnimation"
              class="chart__timer-text"
              :class="{ 'chart__timer-text--danger': isDanger }"
              :initial="{ opacity: 0, scale: 0, x: 0, y: 0 }"
              :animate="{ opacity: 1, scale: 1, x: 0, y: 0 }"
              :transition="{
                type: 'spring',
                stiffness: 200,
                damping: 15,
                mass: 0.8,
                bounce: 0.3,
              }"
            >
              <!-- 남은 시간 숫자 -->
              <motion.span
                class="chart__timer-num"
                :key="displayTime"
                :initial="isDanger ? { scale: 1.5 } : {}"
                :animate="isDanger ? { scale: 1 } : {}"
                :transition="{
                  type: 'spring',
                  stiffness: 400,
                  damping: 7,
                  mass: 0.8,
                  bounce: 0.5,
                }"
              >
                {{ displayTime }}
              </motion.span>
              <!-- 시간 단위 -->
              <span class="chart__timer-unit">초</span>
            </motion.div>

            <!-- 게임 시작 전 대기 아이콘 (바 차트) -->
            <div class="chart__timer-circle-group" v-if="!shouldStartAnimation">
              <motion.i
                v-for="(bar, index) in 5"
                :key="index"
                class="chart__timer-circle-bar"
                :animate="{ height: isMobile ? [10, 30, 10] : [10, 40, 10] }"
                :transition="{
                  duration: 1,
                  repeat: Infinity,
                  ease: [0.42, 0, 0.58, 1.2],
                  delay: index * 0.12,
                }"
              ></motion.i>
            </div>
          </div>

          <!-- DOWN 배팅 카드 -->
          <div class="chart__stat-card chart__stat-card--down">
            <!-- DOWN 결과 표시 영역 -->
            <div class="chart__stat-result">
              <div class="chart__stats-group">
                <!-- DOWN 배지 -->
                <span class="badge badge--down ico-after ico-down"
                  >Down 패배</span
                >
                <!-- 내가 DOWN에 베팅했을 때 표시되는 아이콘 -->
                <motion.div
                  v-if="bettingDirections.includes('down')"
                  :initial="{ opacity: 0, scale: 0, x: 0, y: 0 }"
                  :animate="{ opacity: 1, scale: 1, x: 0, y: 0 }"
                  :transition="{
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                    mass: 0.8,
                    bounce: 0.6,
                  }"
                >
                  <img src="@/assets/images/user.png" alt="" />
                </motion.div>
              </div>
              <!-- DOWN 총 배팅금 (K 단위) -->
              <strong class="chart__stat-value point-secondary">
                <CommonRollingCounter
                  :value="Math.floor(bettingStats.downLose / 1000)"
                  suffix="K"
                  typography="h1"
                />
              </strong>
            </div>

            <!-- DOWN 배팅 정보 (총 배팅금, 예상 수익) -->
            <dl class="chart__stat-info">
              <dt>총 배팅금</dt>
              <dd>
                <CommonRollingCounter
                  :value="bettingStats.downTotalBetting"
                  prefix="₩"
                  typography="h4"
                />
              </dd>
              <dt v-if="!isMobile">예상 수익</dt>
              <dd v-if="!isMobile">
                <CommonRollingCounter
                  :value="bettingStats.downExpectedProfit"
                  prefix="₩"
                  typography="h4"
                />
              </dd>
            </dl>
          </div>
        </div>
      </section>

      <!-- 차트 그래프 영역 -->
      <div class="chart__section-wrapper">
        <section class="chart__section chart__section--graph">
          <!-- 차트 iframe -->
          <div class="chart__canvas">
            <iframe
              id="bcChartIframe"
              src="/btc/index.php"
              style="width: 100%; height: 100%; border: none; background: #000;"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              loading="eager"
            ></iframe>
          </div>

          <!-- 차트 메타 정보 영역 -->
          <div class="chart__meta">
            <!-- 차트 통계 정보 -->
            <div class="chart__meta-stats">
              <!-- 24시간 승률 (데스크탑만) -->
              <div class="chart__meta-info" v-if="!isMobile">
                <strong class="chart__meta-value">
                  <CommonRollingCounter
                    :value="chartMeta.winRate"
                    suffix=" %"
                    typography="h4"
                  />
                </strong>
                <span class="chart__meta-label">24시간 승률</span>
              </div>
              <!-- 24시간 실시간 플레이어 -->
              <div class="chart__meta-info">
                <strong class="chart__meta-value">
                  <CommonRollingCounter
                    :value="chartMeta.realtimePlayers"
                    suffix=" 명"
                    typography="h4"
                  />
                </strong>
                <span class="chart__meta-label">24시간 실시간 플레이어</span>
              </div>
              <!-- 24시간 승리 지급액 (데스크탑만) -->
              <div class="chart__meta-info" v-if="!isMobile">
                <strong class="chart__meta-value">
                  <CommonRollingCounter
                    :value="chartMeta.dailyPayout"
                    prefix="₩"
                    typography="h4"
                  />
                </strong>
                <span class="chart__meta-label">24시간 승리 지급액</span>
              </div>
              <!-- 총 지급된 승리 금액 -->
              <div class="chart__meta-info">
                <strong class="chart__meta-value">
                  <CommonRollingCounter
                    :value="chartMeta.totalPayout"
                    prefix="₩"
                    typography="h4"
                  />
                </strong>
                <span class="chart__meta-label">총 지급된 승리 금액</span>
              </div>
            </div>

            <!-- 게임 히스토리 (승리/패배 기록) -->
            <ul class="chart__history">
              <li
                v-for="item in sortedHistoryData"
                :key="item.id"
                :data-history="item.id"
                :class="[
                  'chart__history-dot',
                  `chart__history-dot--${item.direction}`,
                  'ico-before',
                  `ico-${item.direction}`,
                  { 'chart__history-dot--latest': item.id === latestGameId },
                ]"
                @mouseenter="hoveredHistoryId = item.id"
                @mouseleave="hoveredHistoryId = null"
              >
                <span class="blind">{{ item.label }}</span>
                <AnimatePresence mode="wait">
                  <motion.div
                    v-if="
                      hoveredHistoryId === item.id &&
                      previousHoveredId !== item.id
                    "
                    :key="`history-info-${hoveredHistoryId}`"
                    class="chart__history-info ico-after ico-polygon"
                    :initial="{ opacity: 0, scale: 0.8, x: 0, y: 0 }"
                    :animate="{ opacity: 1, scale: 1, x: 0, y: 0 }"
                    :exit="{ opacity: 0, scale: 0.8, x: 0, y: 0 }"
                    :transition="{
                      type: 'spring',
                      stiffness: 400,
                      damping: 15,
                      mass: 0.8,
                      bounce: 0.6,
                    }"
                  >
                    <div
                      class="chart__history-info-value chart__history-info-value--start"
                    >
                      <span>시작 요율</span>
                      <strong>{{ item.startRate }}</strong>
                    </div>
                    <div
                      class="chart__history-info-value chart__history-info-value--end"
                    >
                      <span>최종 요율</span>
                      <strong>{{ item.endRate }}</strong>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </li>
            </ul>
          </div>
        </section>
      </div>

      <!-- 베팅 상태 영역 (조건부 표시) -->
      <!-- 모바일에서만 표시 (현재는 사용 안함) -->
      <!--
      <section v-if="props.showBettingStatus" class="betting-status">
        <MainBettingStatus />
      </section>
      -->

      <!-- 배팅 입력 영역 -->
      <section v-if="isLoggedIn" class="chart__section chart__section--betting">
        <!-- 배경 장식 요소 -->
        <div class="chart__decorations">
          <div class="blob blob--red"></div>
          <div class="blob blob--green"></div>
        </div>

        <!-- 총 배팅금 미리보기 -->
        <div class="chart__bet-summary">
          <span class="chart__bet-label">총 배팅금</span>
          <strong class="chart__bet-total">
            <CommonRollingCounter
              :value="previewTotalBetting"
              prefix="₩"
              typography="p1"
            />
          </strong>
        </div>

        <!-- 금액 입력 영역 -->
        <div class="chart__input-group">
          <div class="chart__amount-wrap">
            <i class="ico-prefix"></i>
            <!-- 금액 직접 입력 input -->
            <input
              class="chart__amount"
              type="text"
              :value="displayInputValue"
              @input="handleInputChange"
              @keypress="handleKeyPress"
              placeholder="금액 입력"
            />
          </div>
          <!-- 입력 초기화 버튼 -->
          <button
            type="button"
            class="chart__btn-reset btn-ghost-light ico-before ico-refresh"
            @click="handleReset"
          >
            초기화
          </button>
        </div>

        <!-- 배팅 컨트롤 영역 -->
        <div class="chart__controls">
          <!-- 금액 선택 버튼 그룹 -->
          <div class="chart__control-group">
            <!-- 10K 버튼 -->
            <div class="btn-blur-wrap">
              <button
                class="btn btn-blur btn-md"
                :class="{ active: getClickCount(10000) > 0 }"
                @click="handleAmountClick(10000)"
              >
                10K
                <span v-if="getClickCount(10000) >= 2" class="chart__chip-badge"
                  >x{{ getClickCount(10000) }}</span
                >
              </button>
            </div>
            <!-- 25K 버튼 -->
            <div class="btn-blur-wrap">
              <button
                class="btn btn-blur btn-md"
                :class="{ active: getClickCount(25000) > 0 }"
                @click="handleAmountClick(25000)"
              >
                25K
                <span v-if="getClickCount(25000) >= 2" class="chart__chip-badge"
                  >x{{ getClickCount(25000) }}</span
                >
              </button>
            </div>
            <!-- 50K 버튼 -->
            <div class="btn-blur-wrap">
              <button
                class="btn btn-blur btn-md"
                :class="{ active: getClickCount(50000) > 0 }"
                @click="handleAmountClick(50000)"
              >
                50K
                <span v-if="getClickCount(50000) >= 2" class="chart__chip-badge"
                  >x{{ getClickCount(50000) }}</span
                >
              </button>
            </div>
            <!-- 100K 버튼 -->
            <div class="btn-blur-wrap">
              <button
                class="btn btn-blur btn-md"
                :class="{ active: getClickCount(100000) > 0 }"
                @click="handleAmountClick(100000)"
              >
                100K
                <span
                  v-if="getClickCount(100000) >= 2"
                  class="chart__chip-badge"
                  >x{{ getClickCount(100000) }}</span
                >
              </button>
            </div>
            <!-- 500K 버튼 -->
            <div class="btn-blur-wrap">
              <button
                class="btn btn-blur btn-md"
                :class="{ active: getClickCount(500000) > 0 }"
                @click="handleAmountClick(500000)"
              >
                500K
                <span
                  v-if="getClickCount(500000) >= 2"
                  class="chart__chip-badge"
                  >x{{ getClickCount(500000) }}</span
                >
              </button>
            </div>
          </div>

          <!-- 방향 선택 버튼 그룹 -->
          <div class="chart__control-group">
            <!-- UP 배팅 버튼 -->
            <button
              type="button"
              class="chart__btn-action chart__btn-action--up btn-gradient btn-lg ico-after ico-up-white"
              :class="{ 'chart__btn-action--disabled': !canBet }"
              :disabled="!canBet"
              :title="isBettingClosed ? '배팅이 마감되었습니다' : ''"
              @click="handleBet('up')"
            >
              UP
            </button>
            <!-- DOWN 배팅 버튼 -->
            <button
              type="button"
              class="chart__btn-action chart__btn-action--down btn-danger btn-lg ico-after ico-down-white"
              :class="{ 'chart__btn-action--disabled': !canBet }"
              :disabled="!canBet"
              :title="isBettingClosed ? '배팅이 마감되었습니다' : ''"
              @click="handleBet('down')"
            >
              DOWN
            </button>
          </div>
        </div>
      </section>

      <!-- 비로그인 상태: 로그인 필요 메시지 -->
      <section v-else class="chart__section chart__section--betting chart__section--login-required">
        <div class="login-required-message">
          <i class="bi bi-lock"></i>
          <p>배팅하려면 로그인이 필요합니다</p>
          <NuxtLink to="/intro" class="btn btn-primary">로그인</NuxtLink>
        </div>
      </section>
    </div>
  </div>

  <!-- 애니메이션 테스트 버튼 -->
  <!-- <div
    v-show="showTestButtons"
    style="
      position: fixed;
      bottom: 0;
      right: 0;
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      z-index: 1000;
      background: rgba(0, 0, 0, 0.8);
      padding: 1.6rem;
      border-radius: 0.8rem;
    "
  >
    <div
      style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.4rem;
      "
    >
      <span style="color: white; font-weight: bold">테스트 버튼</span>
      <button
        type="button"
        @click="showTestButtons = false"
        style="
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 2.4rem;
          height: 2.4rem;
          border-radius: 0.4rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.6rem;
          line-height: 1;
        "
      >
        ×
      </button>
    </div>
    <div style="display: flex; gap: 0.8rem; flex-wrap: wrap">
      <button
        type="button"
        @click="() => syncAndStartGame('NEW')"
        class="btn-primary"
      >
        게임 시작 (New Game)
      </button>
      <button
        type="button"
        @click="() => syncAndStartGame('MID')"
        class="btn-primary"
      >
        중간 입장 (10초 지남 / 5초 남음)
      </button>
      <button
        type="button"
        @click="() => startNewGameEmpty('NEW')"
        class="btn-primary"
      >
        2번째 게임 시작 (빈 데이터)
      </button>
    </div>
  </div> -->

  <AnimatePresence>
    <motion.div
      class="betting-success ico-before ico-check"
      v-if="showBettingSuccess"
      :initial="{
        opacity: 0,
        x: -100,
      }"
      :animate="{
        opacity: 1,
        x: 0,
        transition: {
          type: 'spring',
          stiffness: 400,
          damping: 20,
        },
      }"
      :exit="{
        opacity: 0,
        x: -100,
        transition: {
          duration: 0.15,
          ease: [0.4, 0, 0.2, 1],
        },
      }"
    >
      <p>주문이 성공적으로 완료되었습니다!</p>
      <button
        type="button"
        class="ico-close"
        @click="showBettingSuccess = false"
      ></button>
    </motion.div>
  </AnimatePresence>

  <Modal ref="playGuideModal" title="작동방식" type="b">
    <template #default="{ close }">
      <div class="modal__content-body">
        <div class="modal__content-guide">
          <motion.div
            v-show="isMobile || currentStep === 1"
            class="modal__content-guide-item"
            :initial="isMobile ? {} : { opacity: 0, x: 50 }"
            :animate="isMobile || currentStep !== 1 ? {} : { opacity: 1, x: 0 }"
            :transition="{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              mass: 0.8,
            }"
          >
            <strong class="modal__content-guide-title"
              ><span>1.</span> 향후 가격 방향 선택</strong
            >
            <div
              class="modal__content-guide-desc modal__content-guide-desc--flex"
            >
              <div class="modal__content-guide-desc-item">
                <div class="modal__content-guide-desc-thumb">
                  <img
                    src="@/assets/images/guide_step1_1.png"
                    alt="CountDown Timer"
                  />
                </div>
                <p>
                  카운트다운의 지정된 시간 내에 주문하려면 <br />
                  위쪽 또는 아래쪽 방향을 선택합니다.
                </p>
              </div>
              <div class="modal__content-guide-desc-item">
                <div class="modal__content-guide-desc-thumb">
                  <img
                    src="@/assets/images/guide_step1_2.png"
                    alt="Betting Up"
                  />
                </div>
                <p>
                  가격이 오를 것 같으면 UP을 선택하여 <br />
                  트렌드와 함께 하세요.
                </p>
              </div>
              <div class="modal__content-guide-desc-item">
                <div class="modal__content-guide-desc-thumb">
                  <img
                    src="@/assets/images/guide_step1_3.png"
                    alt="Betting Down"
                  />
                </div>
                <p>
                  가격이 하락할 것 같으면 DOWN를 선택해 <br />
                  하락을 잡으세요.
                </p>
              </div>
            </div>
            </motion.div>
            <motion.div
              v-show="isMobile || currentStep === 2"
              key="step-2"
              class="modal__content-guide-item"
              :initial="isMobile ? {} : { opacity: 0, x: 50 }"
              :animate="isMobile || currentStep !== 2 ? {} : { opacity: 1, x: 0 }"
              :transition="{
                type: 'spring',
                stiffness: 300,
                damping: 30,
                mass: 0.8,
              }"
            >
            <strong class="modal__content-guide-title"
              ><span>2.</span> UP과 DOWN 방향에서 펀드 풀의 총
              주문 금액과 목록에서 본인 및 다른 사용자의 주문 상태를 확인할 수
              있습니다.</strong
            >
            <div class="modal__content-guide-desc">
              <img src="@/assets/images/guide_step2_1.png" alt="Fund Pool" />
            </div>
          </motion.div>
          <motion.div
            v-show="isMobile || currentStep === 3"
            class="modal__content-guide-item"
            :initial="isMobile ? {} : { opacity: 0, x: 50 }"
            :animate="isMobile || currentStep !== 3 ? {} : { opacity: 1, x: 0 }"
            :transition="{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              mass: 0.8,
            }"
          >
            <strong class="modal__content-guide-title"
              ><span>3.</span> 시스템 정산 대기</strong
            >
            <div class="modal__content-guide-desc">
              <div class="modal__content-guide-desc-item">
                <img
                  src="@/assets/images/guide_step3_1.png"
                  alt="System Settlement"
                  class="modal__content-guide-img-fit"
                />
                <p>
                  카운트다운이 종료되면 준비 정산 프로세스에 들어갑니다.
                  시스템은 카운트다운이 종료된 후 시작가(첫 번째 플래그)를
                  기록합니다. <br />
                  K-라인이 일정 시간 동안 실행되면 시스템은 종료 가격(두 번째
                  플래그)을 기록합니다. 종료 가격과 시작 가격을 비교합니다.
                  <br />
                  종가가 시작가보다 높으면 상승 방향을 선택한 트레이더가
                  승리합니다. 그렇지 않으면 DOWN 방향을 선택한 트레이더가
                  승리합니다.
                </p>
              </div>
            </div>
          </motion.div>
          <motion.div
            v-show="isMobile || currentStep === 4"
            class="modal__content-guide-item"
            :initial="isMobile ? {} : { opacity: 0, x: 50 }"
            :animate="isMobile || currentStep !== 4 ? {} : { opacity: 1, x: 0 }"
            :transition="{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              mass: 0.8,
            }"
          >
            <strong class="modal__content-guide-title"
              ><span>4.</span> 향후 가격 방향 선택</strong
            >
            <div class="modal__content-guide-desc">
              <div class="modal__content-guide-desc-item">
                <div class="modal__content-guide-thumb-wrap">
                  <img
                    src="@/assets/images/guide_step4_1.png"
                    alt="System Settlement"
                  />
                  <img
                    src="@/assets/images/guide_step4_2.png"
                    alt="System Settlement"
                  />
                </div>
                <p>
                  시스템은 업/다운 자금 풀의 승패 결과를 표시합니다. 수익이 발생하면 수익 금액이 표시됩니다.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <div v-if="!isMobile" class="modal__content-footer modal__content-footer--between">
        <div class="modal__content-guide-pagination">
          <span>{{ currentStep }}</span
          >/<span>{{ totalSteps }}</span>
        </div>
        <div class="modal__content-guide-nav">
          <button
            type="button"
            class="btn-gray btn-md-plus"
            :class="{ disabled: isFirstStep }"
            @click="goToPreviousStep"
          >
            이전
          </button>
          <button
            type="button"
            class="btn-gray btn-md-plus"
            :class="{ disabled: isLastStep }"
            @click="goToNextStep"
          >
            다음
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>
<style scoped></style>
